import type { ChangeEvent, FocusEvent } from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { styled } from 'styled-components';

import { apiClientFetchSynonymSearchApiKey } from '#/api/apiClientFetchSynonymSearchApiKey';
import type { Suggestion } from '#/components/Autocomplete/AutocompleteTextInput';
import { Box } from '#/components/Box/Box';
import { Button } from '#/components/Button/Button';
import { InfoBox } from '#/components/InfoBox/InfoBox';
import { LegacyTooltip } from '#/components/LegacyTooltip/LegacyTooltip';
import { useAppConfig } from '#/config/AppConfigContext';
import { useI18n } from '#/i18n/hooks/useI18n';
import { JobSelector } from '#/modules/salary/components/JobSelector/JobSelector';
import type { SalariesJobTitle } from '#/modules/salary/types/SalariesJobTitle';
import { getSearchAlgoliaConfigWithApiKey } from '#/modules/search/algolia/searchAlgoliaConfig';

import { AutocompleteTextInputField } from './AutocompleteTextInputField';
import { useFormContext } from './FormContext';
import { InputField } from './InputField';

const sanitizeJobTitle = (value: string) =>
  value
    .toUpperCase()
    .replace(/[^A-Z\s]/g, '')
    .trim()
    .replace(/\s+/g, ' ');

const JobTitleInputContainer = styled(Box)<{ $editing?: boolean }>`
  ${(props) =>
    props.$editing &&
    `
        input {
          padding-right: 48px;
        }
      `}
`;

const description = 'Start typing to see suggestions';
const placeholder = 'Program Manager';

type Props = {
  searchApiKey: string;
};

type JobTitleSelectorValues = {
  titleSuggestion?: string | null;
  jobTitle?: string | null;
  jobTitleId?: string | null;
  selectedSynonymId?: string | null;
  selectedSynonymFamilyId?: string | null;
  text?: string | null;
  jobTitleMatch?: string | null;
};

type SalarySuggestionValue = {
  name: string;
  objectID: string;
  jobTitle: SalariesJobTitle;
};

type SalarySuggestion = Suggestion & {
  value: SalarySuggestionValue;
  name: string;
};

function isSalarySuggestion(value: unknown): value is SalarySuggestion {
  if (!value || typeof value !== 'object') return false;
  const suggestion = value as Record<string, unknown>;
  const suggestionValue = suggestion.value;
  if (!suggestionValue || typeof suggestionValue !== 'object') return false;

  const { jobTitle } = suggestionValue as Record<string, unknown>;
  return (
    typeof suggestion.name === 'string' &&
    typeof (suggestionValue as Record<string, unknown>).name === 'string' &&
    typeof (suggestionValue as Record<string, unknown>).objectID === 'string' &&
    Boolean(jobTitle) &&
    typeof jobTitle === 'object' &&
    typeof (jobTitle as Record<string, unknown>).familyId === 'string' &&
    typeof (jobTitle as Record<string, unknown>).id === 'string'
  );
}

export function JobTitleSelectorField({ searchApiKey }: Props) {
  const { appConfig } = useAppConfig();
  const { i18n } = useI18n();

  const jobTitleRef = useRef<HTMLDivElement | null>(null);
  const jobTitleMatchRef = useRef<HTMLDivElement | null>(null);
  const { updateValues, values, clearErrors } =
    useFormContext<JobTitleSelectorValues>();
  const [showPrompt, setShowPrompt] = useState(Boolean(values.titleSuggestion));
  const [showError, setShowError] = useState(false);
  const [editing, setEditing] = useState(false);
  const [preselected, setPreselected] = useState(!values.titleSuggestion);
  const [renderSuggestions, setRenderSuggestions] = useState(!values.jobTitle);
  const [showSelector, setShowSelector] = useState(false);
  const [showFreeTextAfter, setShowFreeTextAfter] = useState(
    Boolean(values.titleSuggestion?.length),
  );
  const [autoSuggestion, setAutoSuggestion] = useState<SalarySuggestionValue>();
  const [apiKey, setApiKey] = useState(searchApiKey);
  const selectedTitle = typeof values.text === 'string' ? values.text : '';
  const editLabelTitle = selectedTitle || 'job title';

  const focusInput = () => {
    if (jobTitleRef?.current) {
      const input =
        jobTitleRef.current.querySelector<HTMLInputElement>('input');
      if (!input) return;
      input.focus();
    }
  };

  const focusMatchInput = () => {
    if (jobTitleMatchRef?.current) {
      const input = jobTitleMatchRef.current.querySelector('input');
      if (!input) return;
      input.focus();
    }
  };

  useEffect(() => {
    if (showPrompt) {
      focusMatchInput();
    } else {
      setEditing(false);
      setPreselected(true);
      updateValues({
        jobTitleMatch: null,
      });
    }
  }, [showPrompt, updateValues]);
  useEffect(() => {
    if (showFreeTextAfter) {
      focusInput();
    }
  }, [showFreeTextAfter]);
  useEffect(() => {
    if (editing) {
      focusMatchInput();
    }
  }, [editing]);

  const setSuggestion = (suggestion: SalarySuggestion) => {
    updateValues({
      selectedSynonymId: suggestion.value.objectID,
      selectedSynonymFamilyId: suggestion.value.jobTitle.familyId,
      text: suggestion.name,
      jobTitle: suggestion.name,
      jobTitleId: suggestion.value.jobTitle.id,
      titleSuggestion: null,
    });
    setShowSelector(true);
    setAutoSuggestion(suggestion.value);
    setShowPrompt(false);
    clearErrors(['selectedSynonymId']);
  };

  const onSuggestionSelected = (
    _: unknown,
    payload: { suggestion: unknown },
  ) => {
    if (!isSalarySuggestion(payload.suggestion)) return;
    setSuggestion(payload.suggestion);
  };

  const setSecondarySuggestion = (suggestion: SalarySuggestion) => {
    updateValues({
      selectedSynonymId: suggestion.value.objectID,
      selectedSynonymFamilyId: suggestion.value.jobTitle.familyId,
      text: suggestion.name,
      jobTitleMatch: suggestion.name,
      jobTitleId: suggestion.value.jobTitle.id,
    });
    setShowSelector(true);
    setShowError(false);
    clearErrors(['selectedSynonymId']);
  };

  const onSecondarySuggestionSelected = (
    _: unknown,
    payload: { suggestion: unknown },
  ) => {
    if (!isSalarySuggestion(payload.suggestion)) return;
    setSecondarySuggestion(payload.suggestion);
  };

  const setFreeText = (jobTitle: string) => {
    if (jobTitle.length) {
      setShowPrompt(true);
    }

    updateValues({
      titleSuggestion: jobTitle,
      jobTitle,
    });
  };

  const onBlur = (
    e: FocusEvent<HTMLInputElement>,
    suggestions?: Suggestion[],
  ) => {
    if (suggestions?.length) {
      if (
        sanitizeJobTitle(e.target.value) ===
        sanitizeJobTitle(suggestions[0].name)
      ) {
        if (isSalarySuggestion(suggestions[0])) {
          setSuggestion(suggestions[0]);
        }
      } else if (!values.jobTitleId) {
        setFreeText(e.target.value);

        if (!editing) {
          if (isSalarySuggestion(suggestions[0])) {
            setSecondarySuggestion(suggestions[0]);
          }
        }
      }
    } else if (!values.jobTitleId) {
      setFreeText(e.target.value);

      if (e.target.value.length) {
        setEditing(true);
        setPreselected(false);
      }
    }
  };

  const onBlurSecondaryField = (
    e: FocusEvent<HTMLInputElement>,
    suggestions?: Suggestion[],
  ) => {
    if (suggestions?.length) {
      if (
        sanitizeJobTitle(e.target.value) ===
        sanitizeJobTitle(suggestions[0].name)
      ) {
        if (isSalarySuggestion(suggestions[0])) {
          setSecondarySuggestion(suggestions[0]);
        }
      } else {
        if (!values.jobTitleId && suggestions.length) {
          if (isSalarySuggestion(suggestions[0])) {
            setAutoSuggestion(suggestions[0].value);
          }
        }

        if (!values.selectedSynonymId) {
          setShowError(true);
        }
      }
    } else {
      if (!values.jobTitleId && suggestions?.length) {
        if (isSalarySuggestion(suggestions[0])) {
          setAutoSuggestion(suggestions[0].value);
        }
      }

      if (!values.selectedSynonymId) {
        setShowError(true);
      }
    }
  };

  const onReset = () => {
    updateValues({
      jobTitleMatch: values.jobTitle,
      jobTitle: null,
    });
    setShowFreeTextAfter(true);
    setShowPrompt(true);
  };

  const onContinue = () => {
    updateValues({
      titleSuggestion: values.jobTitle,
    });
    setShowPrompt(true);
  };

  const onResetSecondary = () => {
    focusMatchInput();
    updateValues({
      selectedSynonymId: null,
      selectedSynonymFamilyId: null,
      jobTitleId: null,
    });
    setShowSelector(false);
  };

  const onSuggestionsUpdated = useCallback(
    ({ suggestions, value }: { suggestions?: Suggestion[]; value: string }) => {
      const suggestionCount = suggestions?.length ?? 0;
      setShowSelector(
        Boolean(
          (suggestionCount === 0 && value.trim().length >= 3) ||
          values.jobTitleId,
        ),
      );
    },
    [values.jobTitleId],
  );

  return (
    <Box mb={30}>
      {!showFreeTextAfter && (
        <div ref={jobTitleRef}>
          <AutocompleteTextInputField
            name="jobTitle"
            label={
              /* TRANSLATORS: Compensation survey */
              <Box display="flex" alignItems="center">
                {/* TODO: Fix this the next time the file is edited. */}
                {/* oxlint-disable-next-line react/jsx-curly-brace-presence */}
                {'Job Title'}
                {/* TODO: Fix this the next time the file is edited. */}
                {/* oxlint-disable-next-line react/jsx-curly-brace-presence */}
                {'*'}
                <Box ml="4px">
                  <LegacyTooltip>
                    {i18n(
                      "Use the field below to see title & description recommendations that may describe your role. Don't worry about making a perfect match! Consider selecting a title that is similar to yours.",
                    )}
                  </LegacyTooltip>
                </Box>
              </Box>
            }
            description={description}
            placeholder={placeholder}
            autocompleteType="SALARY_SURVEY_TITLES"
            shouldRenderSuggestions={(v: unknown) =>
              renderSuggestions &&
              (typeof v === 'string' || typeof v === 'number') &&
              v.toString().trim().length >= 3
            }
            onBlur={onBlur}
            onSuggestionSelected={onSuggestionSelected}
            onSuggestionsUpdated={
              !showPrompt ? onSuggestionsUpdated : undefined
            }
            onChange={(e: ChangeEvent<HTMLInputElement>) => {
              if (!renderSuggestions) {
                setRenderSuggestions(true);
              }

              if (showPrompt) {
                updateValues({
                  titleSuggestion: e.target.value,
                  jobTitle: e.target.value,
                });
              } else {
                updateValues({
                  selectedSynonymId: null,
                  selectedSynonymFamilyId: null,
                  text: null,
                  jobTitle: e.target.value,
                  jobTitleId: null,
                });
                setShowSelector(false);
              }
            }}
            customErrorMatch={(errors) =>
              showPrompt
                ? errors.titleSuggestion
                : (errors.text ?? errors.selectedSynonymId)
            }
            large
            searchConfig={getSearchAlgoliaConfigWithApiKey({
              apiKey,
              appConfig,
            })}
            updateApiKey={() =>
              apiClientFetchSynonymSearchApiKey().then((newApiKey) => {
                setApiKey(newApiKey);
                return newApiKey;
              })
            }
          />
          {showSelector &&
            !showPrompt &&
            (values.jobTitleId ??
              (values.jobTitle &&
                values.jobTitle.length >= 3 &&
                !values.jobTitleId)) && (
              <Box mt={-22} minHeight={1}>
                <JobSelector
                  id={values.jobTitleId}
                  title={values.jobTitle ?? ''}
                  onSelect={({ name, id, synonymId, familyId }) => {
                    updateValues({
                      selectedSynonymId: synonymId,
                      selectedSynonymFamilyId: familyId,
                      jobTitle: name,
                      jobTitleId: id,
                    });
                  }}
                  onContinue={onContinue}
                  onReset={onReset}
                />
              </Box>
            )}
        </div>
      )}

      {showPrompt && (
        <>
          <div
            ref={jobTitleMatchRef}
            style={{
              position: 'relative',
            }}
          >
            <AutocompleteTextInputField
              name="jobTitleMatch"
              label={showFreeTextAfter && 'Job Title*'}
              description={
                !showFreeTextAfter && (
                  <InfoBox $boxStyle="form" $type="warning">
                    {`It looks like your job title isn't in our database—sorry about
                    that!
                  ${
                    preselected
                      ? `In order to provide the most detailed survey results,
                      we've selected a title for you.`
                      : `In order to complete the survey, we ask that you
                      still select a title below.`
                  }`}
                  </InfoBox>
                )
              }
              placeholder={placeholder}
              autocompleteType="SALARY_SURVEY_TITLES"
              shouldRenderSuggestions={(v: unknown) =>
                (typeof v === 'string' || typeof v === 'number') &&
                v.toString().trim().length >= 3
              }
              onBlur={onBlurSecondaryField}
              onSuggestionSelected={onSecondarySuggestionSelected}
              onSuggestionsUpdated={onSuggestionsUpdated}
              onChange={(e: ChangeEvent<HTMLInputElement>) => {
                updateValues({
                  selectedSynonymId: null,
                  text: e.target.value,
                  jobTitleMatch: e.target.value,
                  jobTitleId: null,
                });
                setShowSelector(false);
              }}
              hasError={showError}
              customErrorMatch={(errors) =>
                errors.text ?? errors.selectedSynonymId
              }
              large
              disabled={!editing}
              searchConfig={getSearchAlgoliaConfigWithApiKey({
                apiKey,
                appConfig,
              })}
              updateApiKey={() =>
                apiClientFetchSynonymSearchApiKey().then((newApiKey) => {
                  setApiKey(newApiKey);
                  return newApiKey;
                })
              }
            />
            {!editing && (
              <JobTitleInputContainer
                position="absolute"
                right="8px"
                bottom={24}
                transform="translateY(50%)"
              >
                <Button
                  type="button"
                  variant="primary"
                  aria-label={`Edit ${editLabelTitle}`}
                  size="small"
                  onClick={() => {
                    setEditing(true);
                    setShowSelector(false);
                  }}
                  qaId="edit-title-button"
                >
                  Edit
                </Button>
              </JobTitleInputContainer>
            )}
          </div>
          {showSelector &&
            editing &&
            (values.jobTitleId ??
              (values.titleSuggestion &&
                values.titleSuggestion.length >= 3 &&
                !values.jobTitleId)) && (
              <Box mt={-22} minHeight={1}>
                <JobSelector
                  id={values.jobTitleId}
                  title={values.jobTitleMatch ?? ''}
                  onSelect={({ name, id, synonymId, familyId }) => {
                    updateValues({
                      selectedSynonymId: synonymId,
                      selectedSynonymFamilyId: familyId,
                      jobTitleMatch: name,
                      jobTitleId: id,
                    });
                  }}
                  onReset={onResetSecondary}
                  suggestion={autoSuggestion}
                />
              </Box>
            )}
          {showFreeTextAfter && (
            <div ref={jobTitleRef}>
              <Box mt={30}>
                <InputField
                  label="Preferred Job Title"
                  name="titleSuggestion"
                  description="Enter your actual job title here. Sorry it's not in our database yet!"
                />
              </Box>
            </div>
          )}
        </>
      )}
    </Box>
  );
}
